import type { PageMetadata } from '@feedback/shared';

const pageLoadedAt = Date.now();

const sessionId = (() => {
  try {
    return crypto.randomUUID();
  } catch {
    return `s-${Math.random().toString(36).slice(2)}${Date.now().toString(36)}`;
  }
})();

/* -------------------------------------------------------------------------- */
/* User-Agent Client Hints                                                     */
/* -------------------------------------------------------------------------- */

interface UaBrand {
  brand: string;
  version: string;
}

interface HighEntropyValues {
  brands?: UaBrand[];
  fullVersionList?: UaBrand[];
  platform?: string;
  platformVersion?: string;
  mobile?: boolean;
}

interface NavigatorUAData {
  brands?: UaBrand[];
  mobile?: boolean;
  platform?: string;
  getHighEntropyValues?(hints: string[]): Promise<HighEntropyValues>;
}

/**
 * Chrome and friends freeze the UA string at a fixed major version, so parsing it
 * yields a plausible-looking but wrong browser version. Client Hints are the only
 * accurate source. Safari and Firefox do not implement them, so we keep the raw
 * UA string as a fallback and let the server present whichever it has.
 */
const IGNORED_BRANDS = /not.a.brand/i;

function pickBrand(brands: UaBrand[] | undefined): UaBrand | undefined {
  if (!brands?.length) return undefined;
  // Chromium sends a deliberate junk brand to break naive parsers; skip it, and
  // prefer a specific browser over the generic "Chromium" entry.
  const real = brands.filter((b) => !IGNORED_BRANDS.test(b.brand));
  return real.find((b) => b.brand !== 'Chromium') ?? real[0] ?? brands[0];
}

async function readClientHints(): Promise<Pick<PageMetadata, 'browser' | 'os' | 'isMobile'>> {
  const uaData = (navigator as Navigator & { userAgentData?: NavigatorUAData }).userAgentData;
  if (!uaData) return {};

  try {
    const hints = await uaData.getHighEntropyValues?.([
      'fullVersionList',
      'platform',
      'platformVersion',
    ]);

    const brand = pickBrand(hints?.fullVersionList ?? uaData.brands);
    const platform = hints?.platform ?? uaData.platform;

    return {
      ...(brand ? { browser: { name: brand.brand.slice(0, 80), version: brand.version.slice(0, 40) } } : {}),
      ...(platform
        ? { os: { name: platform.slice(0, 80), version: (hints?.platformVersion ?? '').slice(0, 40) } }
        : {}),
      ...(typeof uaData.mobile === 'boolean' ? { isMobile: uaData.mobile } : {}),
    };
  } catch {
    return {};
  }
}

/* -------------------------------------------------------------------------- */

function safeMatchMedia(query: string): boolean {
  try {
    return window.matchMedia(query).matches;
  } catch {
    return false;
  }
}

export async function collectEnvironment(): Promise<PageMetadata> {
  const clientHints = await readClientHints();

  return {
    url: location.href.slice(0, 2_000),
    pageTitle: document.title.slice(0, 500),
    ...(document.referrer ? { referrer: document.referrer.slice(0, 2_000) } : {}),

    viewport: {
      width: Math.round(window.innerWidth),
      height: Math.round(window.innerHeight),
    },
    screen: {
      width: Math.round(window.screen?.width ?? 0),
      height: Math.round(window.screen?.height ?? 0),
    },
    devicePixelRatio: window.devicePixelRatio || 1,
    scroll: {
      x: Math.round(window.scrollX),
      y: Math.round(window.scrollY),
    },

    userAgent: navigator.userAgent.slice(0, 1_000),
    ...clientHints,

    language: (navigator.language || 'unknown').slice(0, 35),
    timezone: (() => {
      try {
        return Intl.DateTimeFormat().resolvedOptions().timeZone ?? 'unknown';
      } catch {
        return 'unknown';
      }
    })().slice(0, 100),
    colorScheme: safeMatchMedia('(prefers-color-scheme: dark)') ? 'dark' : 'light',
    reducedMotion: safeMatchMedia('(prefers-reduced-motion: reduce)'),

    timeOnPageMs: Date.now() - pageLoadedAt,
    sessionId,
  };
}
